import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { Stack, Plus, Check, PencilSimple, Trash, Spinner, CaretDown, Sparkle, Warning } from '@phosphor-icons/react';

export const VersionBar = ({ caseId, initialVersions, initialActive, onChanged }) => {
  const [versions, setVersions] = useState(initialVersions || []);
  const [active, setActive] = useState(initialActive || 1);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmNew, setConfirmNew] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}/versions`);
      setVersions(data.versions || []);
      setActive(data.active_version || 1);
    } catch (err) {
      logger.error('failed to load versions:', err);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const activeMeta = versions.find(v => v.version === active) || { version: active, label: `Version ${active}` };

  const createVersion = async () => {
    setBusy(true); setConfirmNew(false); setOpen(false);
    try {
      await api.post(`/cases/${caseId}/versions`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create version');
    } finally { setBusy(false); }
  };

  const activate = async (v) => {
    if (v === active) { setOpen(false); return; }
    setBusy(true); setOpen(false);
    try {
      await api.post(`/cases/${caseId}/versions/${v}/activate`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to switch version');
    } finally { setBusy(false); }
  };

  const saveRename = async (v) => {
    const label = renameVal.trim();
    setRenaming(null);
    if (!label) return;
    setVersions(prev => prev.map(x => (x.version === v ? { ...x, label } : x)));
    try {
      await api.patch(`/cases/${caseId}/versions/${v}`, { label });
    } catch (err) { logger.error('rename failed:', err); load(); }
  };

  const removeVersion = async (v) => {
    if (!window.confirm(`Delete "${versions.find(x => x.version === v)?.label}"? This permanently removes that version's analysis.`)) return;
    try {
      await api.delete(`/cases/${caseId}/versions/${v}`);
      await load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete version'); }
  };

  return (
    <div className="relative mb-4" data-testid="version-bar">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-ibm font-semibold uppercase tracking-wider text-[#6B7280] flex items-center gap-1">
          <Stack size={14} className="text-[#002FA7]" /> Investigation
        </span>
        <div className="relative">
          <button onClick={() => setOpen(o => !o)} disabled={busy} data-testid="version-selector"
            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-md text-sm font-ibm font-semibold text-[#09090B] hover:border-[#002FA7] transition-all disabled:opacity-50">
            {busy ? <Spinner size={14} className="animate-spin text-[#002FA7]" /> : <span className="w-2 h-2 rounded-full bg-green-500" />}
            {activeMeta.label}
            <CaretDown size={13} className="text-[#6B7280]" />
          </button>
          {open && (
            <div className="absolute z-30 mt-1 w-72 bg-white border border-[#E5E7EB] rounded-lg shadow-xl p-1.5" data-testid="version-dropdown">
              {versions.map(v => (
                <div key={v.version} className={`flex items-center gap-1 px-2 py-1.5 rounded-md ${v.version === active ? 'bg-[#002FA7]/5' : 'hover:bg-[#F3F4F6]'}`} data-testid={`version-item-${v.version}`}>
                  {renaming === v.version ? (
                    <input autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveRename(v.version); if (e.key === 'Escape') setRenaming(null); }}
                      onBlur={() => saveRename(v.version)}
                      className="flex-1 px-2 py-1 text-sm font-ibm border border-[#002FA7] rounded" data-testid={`version-rename-input-${v.version}`} />
                  ) : (
                    <button onClick={() => activate(v.version)} className="flex-1 flex items-center gap-2 text-left text-sm font-ibm text-[#09090B]" data-testid={`version-activate-${v.version}`}>
                      {v.version === active ? <Check size={14} className="text-green-600 shrink-0" weight="bold" /> : <span className="w-3.5 shrink-0" />}
                      <span className="truncate">{v.label}</span>
                    </button>
                  )}
                  <button onClick={() => { setRenaming(v.version); setRenameVal(v.label); }} title="Rename" data-testid={`version-rename-${v.version}`}
                    className="p-1 text-[#6B7280] hover:text-[#002FA7]"><PencilSimple size={13} /></button>
                  {versions.length > 1 && v.version !== active && (
                    <button onClick={() => removeVersion(v.version)} title="Delete" data-testid={`version-delete-${v.version}`}
                      className="p-1 text-[#6B7280] hover:text-red-600"><Trash size={13} /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <button onClick={() => setConfirmNew(true)} disabled={busy} data-testid="version-new-button"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#002FA7] text-white rounded-md text-sm font-ibm font-semibold hover:bg-[#00237D] transition-all disabled:opacity-50">
          <Plus size={14} /> Fresh analysis
        </button>
      </div>

      {confirmNew && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="version-confirm-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="font-outfit text-lg font-semibold text-[#09090B] flex items-center gap-2"><Sparkle size={20} className="text-[#002FA7]" weight="fill" /> Start a fresh analysis?</h3>
            <p className="text-sm text-[#6B7280] font-ibm mt-2">
              This creates a brand-new investigation version and re-analyzes <strong>all uploaded documents from scratch</strong>. The new version is fully independent — none of the current version's AI analysis, narration, generated documents or chat is carried over. Your current version is saved and you can switch back anytime.
            </p>
            <div className="flex items-center gap-2 mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 font-ibm">
              <Warning size={15} /> Uploaded documents are shared across versions; only the analysis is regenerated.
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setConfirmNew(false)} className="px-4 py-2 text-sm font-ibm font-semibold text-[#6B7280] hover:bg-[#F3F4F6] rounded-md">Cancel</button>
              <button onClick={createVersion} data-testid="version-confirm-create" className="px-4 py-2 text-sm font-ibm font-semibold bg-[#002FA7] text-white rounded-md hover:bg-[#00237D]">Create & analyze</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
